import React, { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import QRCode from 'qrcode';

import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';

import QRCodeColorPicker from '../QRCodeColorPicker';

interface QRCodeModalProps {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
}

const QRCodeModal: FC<QRCodeModalProps> = ({ open, onClose, url, title = 'QR-код конструктора' }) => {
  const { t } = useTranslation();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null); // null = прозрачный
  const [foregroundColor, setForegroundColor] = useState<string>('#000000'); // Цвет QR-кода

  const generateQRCode = useCallback(async (urlToGenerate: string, bgColor: string | null = null, fgColor: string = '#000000') => {
    if (!urlToGenerate) {
      console.error('URL для QR-кода не предоставлен');
      return;
    }

    setIsGenerating(true);
    setQrCodeDataUrl('');

    try {
      // Создаем временный canvas для обработки
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 300;
      tempCanvas.height = 300;

      // Генерируем QR-код на временном canvas
      await QRCode.toCanvas(tempCanvas, urlToGenerate, {
        width: 300,
        margin: 2,
        color: {
          dark: fgColor,
          light: bgColor || '#FFFFFF',
        },
        errorCorrectionLevel: 'M',
      });

      const ctx = tempCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Не удалось получить контекст canvas');
      }

      // Если выбран прозрачный фон, делаем белые пиксели прозрачными
      if (bgColor === null) {
        const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imageData.data;

        // Проходим по всем пикселям и делаем белые пиксели прозрачными
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          // Если пиксель белый (или почти белый), делаем его прозрачным
          if (r > 250 && g > 250 && b > 250) {
            data[i + 3] = 0; // Устанавливаем альфа-канал в 0 (прозрачный)
          }
        }

        ctx.putImageData(imageData, 0, 0);
      }

      // Конвертируем canvas в data URL
      const dataUrl = tempCanvas.toDataURL('image/png');
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Ошибка генерации QR-кода:', err);
      setQrCodeDataUrl('');
    } finally {
      setIsGenerating(false);
    }
  }, []);

  useEffect(() => {
    if (open && url) {
      // Небольшая задержка, чтобы убедиться, что canvas готов
      const timer = setTimeout(() => {
        generateQRCode(url, backgroundColor, foregroundColor);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setQrCodeDataUrl('');
    }
  }, [open, url, backgroundColor, foregroundColor, generateQRCode]);

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const link = document.createElement('a');
    link.download = `qr-code-constructor.png`;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyQRCode = async () => {
    if (!qrCodeDataUrl) return;

    try {
      // Конвертируем data URL в blob
      const response = await fetch(qrCodeDataUrl);
      const blob = await response.blob();
      
      // Копируем blob в буфер обмена
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Ошибка копирования QR-кода:', err);
      // Fallback: скачиваем файл
      downloadQRCode();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          {isGenerating ? (
            <Typography variant="body2" color="text.secondary">
              Генерируем QR-код...
            </Typography>
          ) : qrCodeDataUrl ? (
            <>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: backgroundColor || 'white',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  display: 'inline-block',
                  // Шахматный фон для визуализации прозрачности (только если фон прозрачный)
                  ...(backgroundColor === null && {
                    backgroundImage: `
                      linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                      linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                      linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                    `,
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                  }),
                }}
              >
                <img
                  src={qrCodeDataUrl}
                  alt="QR-код"
                  style={{ 
                    width: '300px', 
                    height: '300px', 
                    display: 'block',
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {url}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Не удалось сгенерировать QR-код
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="column" spacing={2} sx={{ width: '100%', pb: 1 }}>
          {/* Компонент выбора цвета фона и QR-кода */}
          <QRCodeColorPicker
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
            onBackgroundColorSelect={setBackgroundColor}
            onForegroundColorSelect={setForegroundColor}
          />

          {/* Кнопки действий */}
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadQRCode}
              disabled={!qrCodeDataUrl}
            >
              Скачать
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={copyQRCode}
              disabled={!qrCodeDataUrl}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Скопировано!' : 'Скопировать'}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeModal;

